import { useEffect, useState } from 'react';
import {
  Button,
  ComposedModal,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from '@carbon/react';
import { defaultWorkspaceName } from './utils';
import styles from './name-workspace-modal.module.scss';

interface Props {
  open: boolean;
  /** How many wells the workspace will hold; the default name counts them. */
  wellCount: number;
  /** True while the create is in flight, to hold the field and the buttons. */
  saving: boolean;
  /** Why the last attempt was refused, or null. Shown inside the modal. */
  error: string | null;
  onClose: () => void;
  /** The name to create with, already trimmed. */
  onSubmit: (name: string) => void;
}

// The small modal a one-click "Create Well Workspace" opens, so the workspace gets its
// name before it exists rather than the backend's stamp. Pre-filled with that stamp —
// "Well Workspace — 1 well — Sep 11, 6:58 PM" — so keeping it costs one click. Built the
// way the pre-drill and type-curve create modals are: a ComposedModal on plain state,
// the name required, the refusal shown in place. The wells listing's own create modal
// carries the same field above its well picker.
export const NameWorkspaceModal = ({
  open,
  wellCount,
  saving,
  error,
  onClose,
  onSubmit,
}: Props) => {
  const [name, setName] = useState('');
  const [nameBlurred, setNameBlurred] = useState(false);

  // Re-seeded each time the modal opens, so a name typed and then cancelled does not
  // come back on the next open.
  useEffect(() => {
    if (open) {
      setName(defaultWorkspaceName(wellCount, new Date()));
      setNameBlurred(false);
    }
  }, [open, wellCount]);

  const nameTrimmed = name.trim();
  const nameInvalid = nameBlurred && nameTrimmed.length === 0;
  const canSubmit = nameTrimmed.length > 0 && !saving;

  const handleClose = () => {
    if (!saving) onClose();
  };

  const handleSubmit = () => {
    if (canSubmit) onSubmit(nameTrimmed);
  };

  return (
    <ComposedModal
      open={open}
      onClose={handleClose}
      preventCloseOnClickOutside={saving}
      size="sm"
      className={styles.modal}
    >
      <ModalHeader label="Well Workspace" title="Create Workspace" />
      <ModalBody aria-label="Create Well Workspace">
        {error ? (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={error}
            lowContrast
            hideCloseButton
            className={styles.notification}
          />
        ) : null}

        <TextInput
          id="name-workspace-modal-name"
          labelText="Workspace name"
          value={name}
          disabled={saving}
          invalid={nameInvalid}
          invalidText="Enter a workspace name."
          onChange={(event) => setName(event.target.value)}
          onBlur={() => setNameBlurred(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') handleSubmit();
          }}
        />
      </ModalBody>
      <ModalFooter>
        <Button
          kind="secondary"
          className={styles.cancelButton}
          onClick={handleClose}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button kind="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {saving ? 'Creating…' : 'Create Workspace'}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};
